<template>
  <v-dialog
    v-model="visible"
    persistent
    width="900"
    content-class="processing-batch-add-dialog"
    @click:outside="close"
  >
    <v-card v-if="dialogReady" rounded="0" class="processing-batch-add-dialog__card">
      <v-toolbar color="primary" class="white--text" flat>
        <v-toolbar-title class="font-weight-bold">
          {{ $t("memberDataSection.processingBatch") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-form
        ref="processingBatchForm"
        autocomplete="off"
        v-model="valid"
        class="processing-batch-add-dialog__form"
        @submit.prevent="openConfirmDialog"
      >
        <v-card-text class="black--text processing-batch-add-dialog__body">
          <v-row>
            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.startDate") }}<span class="red--text">*</span></label>
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="processingBatch.startDate"
                    append-icon="mdi-calendar"
                    outlined
                    dense
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    v-on:click:append="startDateMenu = true"
                    :rules="processingBatchRules.startDate"
                    :placeholder="$t('memberDataSection.startDate')"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="processingBatch.startDate"
                  @input="startDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.endDate") }}<span class="red--text">*</span></label>
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="processingBatch.endDate"
                    append-icon="mdi-calendar"
                    outlined
                    dense
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    v-on:click:append="endDateMenu = true"
                    :rules="processingBatchRules.endDate"
                    :placeholder="$t('memberDataSection.endDate')"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="processingBatch.endDate"
                  @input="endDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.purchasingOrder") }}<span class="red--text">*</span></label>
              <v-select
                v-model="processingBatch.buyingStationOrder"
                multiple
                :items="purchaseOrder"
                item-value="id"
                item-text="orderCode"
                return-object
                outlined
                dense
                :placeholder="$t('MakeASelection')"
                :rules="processingBatchRules.buyingStationOrder"
                :disabled="!isAddMode"
              ></v-select>
              <v-simple-table
                v-if="processingBatch.buyingStationOrder && processingBatch.buyingStationOrder.length !== 0"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">{{ $t("farmerLotID") }}</th>
                      <th class="text-left">{{ $t("weight") }}</th>
                      <th class="text-left">{{ $t("price") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in processingBatch.buyingStationOrder"
                      :key="item.id"
                    >
                      <td>
                        <div>
                          {{
                            item.farmer
                              ? item.farmer.firstName
                              : item.buyingStation.firstName
                          }}
                          {{
                            item.farmer
                              ? item.farmer.lastName
                              : item.buyingStation.lastName
                          }}
                        </div>
                        <div>{{ item.orderCode }}</div>
                      </td>
                      <td>{{ item.coffeeCherryQty }} KG</td>
                      <td>$ {{ item.grandTotal }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <div class="d-flex justify-space-between align-end">
                <label>{{ $t("memberDataSection.totalCoffeeCherry") }}<span class="red--text">*</span></label>
                <label>KG</label>
              </div>
              <v-text-field
                outlined
                dense
                type="number"
                min="0"
                :value="totalCoffeeCherryWeight"
                :rules="processingBatchRules.totalCoffeeCherryQty"
                :placeholder="$t('typeTotalCoffeeQuantity')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <div class="d-flex justify-space-between align-end">
                <label>{{ $t("memberDataSection.airHumidity") }}</label>
                <label>{{ $t("valueInPercent") }}</label>
              </div>
              <v-text-field
                v-model="processingBatch.humidity"
                outlined
                dense
                type="number"
                min="0"
                :placeholder="$t('typeHumidity')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <div class="d-flex justify-space-between align-end">
                <label>{{ $t("memberDataSection.density") }}</label>
                <label>KG/cm3</label>
              </div>
              <v-text-field
                v-model="processingBatch.density"
                outlined
                dense
                type="number"
                min="0"
                :placeholder="$t('memberDataSection.pOrder.typeDensity')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.temperature") }}</label>
              <v-text-field
                v-model="processingBatch.temperature"
                outlined
                dense
                type="number"
                min="0"
                :placeholder="$t('memberDataSection.pOrder.typeTemperature')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <div class="d-flex justify-space-between align-end">
                <label>{{ $t("memberDataSection.beanMoistureContent") }}</label>
                <label>{{ $t("valueInPercent") }}</label>
              </div>
              <v-text-field
                v-model="processingBatch.waterContent"
                outlined
                dense
                type="number"
                min="0"
                :placeholder="$t('memberDataSection.pOrder.typeWaterContent')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.selectBatchRating") }}</label>
              <v-select
                v-model="processingBatch.batchRating"
                :items="processingBatchQuality"
                item-text="name"
                item-value="name"
                outlined
                dense
                :loading="referenceDataLoading"
                :placeholder="$t('MakeASelection')"
              ></v-select>
            </v-col>
            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.processingBatchBulkImport.gradeCol") }}</label>
              <v-text-field
                v-model="processingBatch.grade"
                outlined
                dense
                :placeholder="$t('memberDataSection.processingBatchBulkImport.gradeCol')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.processingBatchBulkImport.seasonCol") }}</label>
              <v-text-field
                v-model="processingBatch.season"
                outlined
                dense
                :placeholder="$t('memberDataSection.processingBatchBulkImport.seasonCol')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.processingBatchBulkImport.bagsCol") }}</label>
              <v-text-field
                v-model="processingBatch.bags"
                outlined
                dense
                type="number"
                min="0"
                :placeholder="$t('memberDataSection.processingBatchBulkImport.bagsCol')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.processingBatchBulkImport.vehiclePlateCol") }}</label>
              <v-text-field
                v-model="processingBatch.vehiclePlate"
                outlined
                dense
                :placeholder="$t('memberDataSection.processingBatchBulkImport.vehiclePlateCol')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.processingBatchBulkImport.harvestedCol") }}</label>
              <v-menu
                v-model="harvestedMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="processingBatch.harvested"
                    append-icon="mdi-calendar"
                    outlined
                    dense
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    v-on:click:append="harvestedMenu = true"
                    :placeholder="$t('memberDataSection.processingBatchBulkImport.harvestedCol')"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="processingBatch.harvested" @input="harvestedMenu = false"></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.processingBatchBulkImport.pulpedCol") }}</label>
              <v-menu
                v-model="pulpedMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="processingBatch.pulped"
                    append-icon="mdi-calendar"
                    outlined
                    dense
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    v-on:click:append="pulpedMenu = true"
                    :placeholder="$t('memberDataSection.processingBatchBulkImport.pulpedCol')"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="processingBatch.pulped" @input="pulpedMenu = false"></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.processingBatchBulkImport.washedCol") }}</label>
              <v-menu
                v-model="washedMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="processingBatch.washed"
                    append-icon="mdi-calendar"
                    outlined
                    dense
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    v-on:click:append="washedMenu = true"
                    :placeholder="$t('memberDataSection.processingBatchBulkImport.washedCol')"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="processingBatch.washed" @input="washedMenu = false"></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.processingBatchBulkImport.dryingStartedCol") }}</label>
              <v-menu
                v-model="dryingStartedMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="processingBatch.dryingStarted"
                    append-icon="mdi-calendar"
                    outlined
                    dense
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    v-on:click:append="dryingStartedMenu = true"
                    :placeholder="$t('memberDataSection.processingBatchBulkImport.dryingStartedCol')"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="processingBatch.dryingStarted" @input="dryingStartedMenu = false"></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label>{{ $t("memberDataSection.processingBatchBulkImport.movedToStoreCol") }}</label>
              <v-menu
                v-model="movedToStoreMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="processingBatch.movedToStore"
                    append-icon="mdi-calendar"
                    outlined
                    dense
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    v-on:click:append="movedToStoreMenu = true"
                    :placeholder="$t('memberDataSection.processingBatchBulkImport.movedToStoreCol')"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="processingBatch.movedToStore" @input="movedToStoreMenu = false"></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" class="px-4 py-0">
              <label>{{ $t("memberDataSection.processType") }}<span class="red--text">*</span></label>
              <v-radio-group
                v-model="processingBatch.processingTypeId"
                row
                :rules="processingBatchRules.processType"
                class="process-type-radio-group"
              >
                <v-radio
                  v-for="item in processTypes"
                  :key="item.id"
                  :label="item.name"
                  :value="Number(item.id)"
                  color="primary"
                ></v-radio>
              </v-radio-group>
            </v-col>

            <v-col cols="12" class="px-4 processing-output-divider-col">
              <v-divider class="mb-4"></v-divider>
            </v-col>

            <v-col cols="12" class="px-4 pt-0">
              <div class="d-flex align-center pb-output-checkbox-row">
                <v-checkbox
                  v-model="parchmentProducedChecked"
                  color="primary"
                  hide-details
                  class="mt-0 pt-0 pb-output-checkbox"
                ></v-checkbox>
                <span class="pb-output-checkbox-label">{{ $t("memberDataSection.parchmentProduced") }}</span>
              </div>
            </v-col>

            <v-col v-if="parchmentProducedChecked" cols="6" class="py-0 px-4">
              <div class="d-flex justify-space-between align-end">
                <label>{{ $t("memberDataSection.parchmentProduced") }}</label>
                <label>KG</label>
              </div>
              <v-text-field
                v-model="parchmentOut"
                outlined
                dense
                type="number"
                min="0"
                :placeholder="$t('enterHere')"
                :rules="parchmentOutRules"
              ></v-text-field>
            </v-col>

            <v-col cols="12" class="px-4">
              <div class="d-flex align-center pb-output-checkbox-row">
                <v-checkbox
                  v-model="huskProducedChecked"
                  color="primary"
                  hide-details
                  class="mt-0 pt-0 pb-output-checkbox"
                ></v-checkbox>
                <span class="pb-output-checkbox-label">{{ $t("memberDataSection.huskProduced") }}</span>
              </div>
            </v-col>

            <v-col v-if="huskProducedChecked" cols="6" class="py-0 px-4">
              <div class="d-flex justify-space-between align-end">
                <label>{{ $t("memberDataSection.huskProducedLabel") }}</label>
                <label>KG</label>
              </div>
              <v-text-field
                v-model="quantityOfHusk"
                outlined
                dense
                type="number"
                min="0"
                :placeholder="$t('enterHere')"
                :rules="quantityOfHuskRules"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>

        <div class="processing-batch-add-dialog__footer">
          <v-card-actions class="processing-batch-add-dialog__actions pt-0 pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn outlined color="primary" width="112" @click="close">
              {{ $t("cancel") }}
            </v-btn>
            <v-btn width="112" color="primary" type="submit">
              {{ $t("submit") }}
            </v-btn>
          </v-card-actions>
        </div>
      </v-form>
    </v-card>

    <ProcessingBatchConfirmDialog
      v-model="confirmVisible"
      :field-rows="confirmationFieldRows"
      :confirming="mixingIsLoading"
      @edit="closeConfirmDialog"
      @confirm="confirmProcessingBatch"
      @close="closeConfirmDialog"
      @quality-grade="openQualityGrading"
    />

    <QualityGradingDialog
      v-model="qualityGradingVisible"
      :total-quantity="qualityGradingTotalQuantity"
      :unit-size="qualityGradingUnitSize"
      :lots="qualityGradingLots"
      :quality-scores="gradingQualityScores"
      :labels="gradingLabels"
      @finish-grading="onQualityGradingFinished"
      @lots-changed="qualityGradingLots = $event"
    />
  </v-dialog>
</template>

<script>
import loading from "@/mixins/LoadingMixin";
import BuyingStationService from "@/_services/BuyingStationService";
import CoffeeService from "@/_services/CoffeeService.js";
import ProcessingBatchConfirmDialog from "./ConfirmDialog.vue";
import QualityGradingDialog from "./QualityGradingDialog.vue";
import moment from "moment";

export default {
  name: "ProcessingBatchAddDialog",
  components: {
    ProcessingBatchConfirmDialog,
    QualityGradingDialog,
  },
  mixins: [loading],
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
    mode: {
      type: String,
      default: "add",
      validator: (v) => ["add", "edit"].includes(v),
    },
    buyingStationId: {
      type: [Number, String],
      default: null,
    },
    usedPurchaseOrders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      visible: this.value,
      confirmVisible: false,
      valid: true,
      startDateMenu: false,
      endDateMenu: false,
      harvestedMenu: false,
      pulpedMenu: false,
      washedMenu: false,
      dryingStartedMenu: false,
      movedToStoreMenu: false,
      processingBatch: {},
      parchmentProducedChecked: false,
      huskProducedChecked: false,
      parchmentOut: "",
      quantityOfHusk: "",
      purchaseOrder: [],
      processTypes: [],
      processingBatchQuality: [],
      referenceDataLoading: false,
      referenceDataLoaded: false,
      qualityGradingVisible: false,
      qualityGradingLots: [],
      gradingQualityScores: [],
      gradingLabels: [],
      processingBatchRules: {
        startDate: [
          (v) => !!v || this.$t("startDateRequired"),
          (v) => {
            if (!v || !this.processingBatch.endDate) return true;
            const start = moment(v);
            const end = moment(this.processingBatch.endDate);
            return start.isBefore(end, "day") || "Start Date must be before End Date";
          },
        ],
        endDate: [(v) => !!v || this.$t("endDateRequired")],
        buyingStationOrder: [(v) => v.length !== 0 || this.$t("purchaseOrderRequired")],
        totalCoffeeCherryQty: [(v) => !!v || this.$t("totalCoffeeCherryRequired")],
        processType: [(v) => !!v || this.$t("processTypeRequired")],
      },
      ratingMapping: {
        Platinum: "A",
        Gold: "B",
        Silver: "C",
        Bronze: "D",
        A: "A",
        B: "B",
        C: "C",
        D: "D",
        E: "E",
      },
    };
  },
  computed: {
    isAddMode() {
      return this.mode === "add";
    },
    dialogReady() {
      return this.isAddMode || Boolean(this.record);
    },
    totalCoffeeCherryWeight() {
      if (this.processingBatch.buyingStationOrder) {
        return this.processingBatch.buyingStationOrder.reduce((prev, next) => {
          return Number(prev) + Number(next.coffeeCherryQty);
        }, 0);
      }
      return 0;
    },
    getOrganizationId() {
      return this.$store.getters.getUser?.user_organization?.id;
    },
    getSubOrganizationId() {
      return this.$store.getters.getUser?.user_organization?.subOrganizationId;
    },
    isSubEnterprise() {
      return this.$store.getters.isSubEnterprise;
    },
    resolvedBuyingStationId() {
      return (
        this.buyingStationId ||
        this.processingBatch.buyingStationId ||
        this.record?.buyingStationId
      );
    },
    parchmentOutRules() {
      if (!this.parchmentProducedChecked) return [];
      return [
        (v) => !!v || this.$t("processedCoffeeOutRequired"),
        (v) =>
          !v ||
          v <= this.totalCoffeeCherryWeight ||
          "Processed Coffee Out value cannot be greater than total coffee quantity.",
      ];
    },
    quantityOfHuskRules() {
      if (!this.huskProducedChecked) return [];
      return [(v) => !!v || this.$t("memberDataSection.huskProducedRequired")];
    },
    confirmationFieldRows() {
      const t = (key) => this.$t(`memberDataSection.${key}`);
      const dash = "—";
      const lots =
        (this.processingBatch.buyingStationOrder || [])
          .map((order) => order.orderCode)
          .filter(Boolean)
          .join(", ") || dash;

      const rows = [
        [
          {
            key: "startDate",
            label: t("startDate"),
            value: this.formatConfirmationDate(this.processingBatch.startDate),
          },
          {
            key: "endDate",
            label: t("endDate"),
            value: this.formatConfirmationDate(this.processingBatch.endDate),
          },
          { key: "lots", label: t("lots"), value: lots },
        ],
        [
          {
            key: "totalCoffeeCherry",
            label: t("totalCoffeeCherry"),
            value: `${this.totalCoffeeCherryWeight} kg`,
          },
          {
            key: "humidity",
            label: t("airHumidity"),
            value: this.formatConfirmationPercent(this.processingBatch.humidity),
          },
          {
            key: "density",
            label: t("density"),
            value: this.formatConfirmationDensity(this.processingBatch.density),
          },
        ],
        [
          {
            key: "temperature",
            label: t("temperature"),
            value: this.formatConfirmationTemperature(this.processingBatch.temperature),
          },
          {
            key: "waterContent",
            label: t("beanMoistureContent"),
            value: this.formatConfirmationPercent(this.processingBatch.waterContent),
          },
          {
            key: "processType",
            label: t("processType"),
            value: this.resolveProcessTypeName(),
          },
        ],
      ];

      const ratingRow = [
        {
          key: "batchRating",
          label: this.$t("memberDataSection.processingBatchReport.ratingOfTheBatch"),
          value: this.processingBatch.batchRating || dash,
        },
      ];

      if (this.parchmentProducedChecked && this.parchmentOut !== "") {
        ratingRow.push({
          key: "parchmentOut",
          label: t("parchmentProduced"),
          value: `${this.parchmentOut} kg`,
        });
      }

      if (this.huskProducedChecked && this.quantityOfHusk !== "") {
        ratingRow.push({
          key: "quantityOfHusk",
          label: t("huskProducedLabel"),
          value: `${this.quantityOfHusk} kg`,
        });
      }

      rows.push(ratingRow);
      return rows;
    },
    qualityGradingTotalQuantity() {
      if (this.parchmentProducedChecked && this.parchmentOut !== "") {
        return Number(this.parchmentOut) || 0;
      }
      return this.totalCoffeeCherryWeight;
    },
    qualityGradingUnitSize() {
      const stored = this.processingBatch.qualityGradingUnitSize;
      if (stored != null && stored !== "") return stored;
      return 40;
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) {
        this.$nextTick(() => {
          this.onOpen();
          this.resetDialogScroll();
        });
      }
    },
    visible(v) {
      this.$emit("input", v);
    },
  },
  mounted() {
    this.loadReferenceData();
  },
  methods: {
    close() {
      this.confirmVisible = false;
      this.resetQualityGrading();
      this.visible = false;
      this.resetProcessingOutputs();
      this.$emit("close");
    },
    closeConfirmDialog() {
      this.confirmVisible = false;
    },
    openConfirmDialog() {
      if (!this.$refs.processingBatchForm.validate()) return;
      this.confirmVisible = true;
    },
    async openQualityGrading() {
      if (!this.qualityGradingTotalQuantity) {
        this.$notify({
          type: "error",
          title: this.$t("memberDataSection.greenCoffee.createQualityGrade"),
          text: this.$t("memberDataSection.processingBatchConfirm.qualityGradingQuantityRequired"),
        });
        return;
      }
      await this.loadGradingOptions();
      this.qualityGradingVisible = true;
    },
    async loadGradingOptions() {
      try {
        const { data } = await this.$http.get("/coffee/greencoffee/options");
        const payload = data?.data || data || {};
        this.gradingQualityScores = payload.greenCoffeeQuality || [];
        this.gradingLabels = payload.labels || [];
      } catch (err) {
        console.log("Processing batch grading options fetch error", err);
      }
    },
    onQualityGradingFinished(lots) {
      this.qualityGradingLots = lots || [];
      this.processingBatch.qualityGradingLots = this.qualityGradingLots;
      this.$notify({
        type: "success",
        title: this.$t("memberDataSection.greenCoffee.finishGrading"),
        text: this.$t("memberDataSection.processingBatchConfirm.qualityGradingFinished"),
      });
    },
    resetQualityGrading() {
      this.qualityGradingVisible = false;
      this.qualityGradingLots = [];
    },
    formatConfirmationDate(value) {
      if (!value) return "—";
      const parsed = moment(
        value,
        [moment.ISO_8601, "DD-MMM-YYYY", "DD-MMM-YY", "MM/DD/YYYY", "YYYY-MM-DD"],
        true
      );
      if (parsed.isValid()) {
        return parsed.format("DD-MMM-YYYY").toUpperCase();
      }
      const fallback = moment(value);
      return fallback.isValid() ? fallback.format("DD-MMM-YYYY").toUpperCase() : value;
    },
    formatConfirmationPercent(value) {
      if (value == null || value === "") return "—";
      return `${value}%`;
    },
    formatConfirmationDensity(value) {
      if (value == null || value === "") return "—";
      return `${value} kg`;
    },
    formatConfirmationTemperature(value) {
      if (value == null || value === "") return "—";
      return `${value} C`;
    },
    resolveProcessTypeName() {
      const typeId = Number(this.processingBatch.processingTypeId);
      const match = this.processTypes.find((item) => Number(item.id) === typeId);
      return match?.name || "—";
    },
    buildSubmitPayload() {
      const payload = {
        startDate: this.processingBatch.startDate,
        endDate: this.processingBatch.endDate,
        totalCoffeeCherryQty: this.totalCoffeeCherryWeight,
        humidity: parseFloat(this.processingBatch.humidity),
        temperature: parseFloat(this.processingBatch.temperature),
        waterContent: parseFloat(this.processingBatch.waterContent),
        processingTypeId: this.processingBatch.processingTypeId,
        batchRating: this.processingBatch.batchRating,
        purchasingNum: this.processingBatch.buyingStationOrder.map((item) => item.id),
        density: parseFloat(this.processingBatch.density),
        buyingStationId: this.processingBatch.buyingStationId,
        grade: this.processingBatch.grade || null,
        season: this.processingBatch.season || null,
        bags: this.processingBatch.bags != null && this.processingBatch.bags !== "" ? parseInt(this.processingBatch.bags) : null,
        vehiclePlate: this.processingBatch.vehiclePlate || null,
        harvested: this.processingBatch.harvested || null,
        pulped: this.processingBatch.pulped || null,
        washed: this.processingBatch.washed || null,
        dryingStarted: this.processingBatch.dryingStarted || null,
        movedToStore: this.processingBatch.movedToStore || null,
      };

      if (this.parchmentProducedChecked && this.parchmentOut !== "") {
        payload.parchmentOut = Number(this.parchmentOut);
      }
      if (this.huskProducedChecked) {
        payload.wasHuskProduced = true;
        if (this.quantityOfHusk !== "") {
          payload.quantityOfHusk = Number(this.quantityOfHusk);
        }
      }

      if (this.qualityGradingLots && this.qualityGradingLots.length) {
        // ponytail: upsert-only — lots removed in edit mode aren't deleted server-side.
        // Only forward a real DB id (integer); the grading dialog assigns temporary
        // string ids (e.g. "12_1782882090750") to new lots, which must create, not update.
        payload.qualityGrading = this.qualityGradingLots.map((lot) => ({
          ...(Number.isInteger(lot.id) && { id: lot.id }),
          lotId: lot.lotId,
          quantity: lot.quantity,
          quantityUnit: "kg",
          unitSize: lot.unitSize,
          unitSizeUnit: "kg",
          qualityScore: lot.qualityScore,
        }));
      }

      if (!this.isAddMode && this.processingBatch.id) {
        payload.batchId = this.processingBatch.id;
      }

      return payload;
    },
    resetDialogScroll() {
      this.$nextTick(() => {
        const body = this.$el?.querySelector(".processing-batch-add-dialog__body");
        if (body) body.scrollTop = 0;
      });
    },
    resetProcessingOutputs() {
      this.parchmentProducedChecked = false;
      this.huskProducedChecked = false;
      this.parchmentOut = "";
      this.quantityOfHusk = "";
      this.resetQualityGrading();
    },
    setProcessingOutputsFromBatch(batch) {
      const data = batch || {};
      this.parchmentProducedChecked = data.parchmentOut != null && data.parchmentOut !== "";
      this.parchmentOut =
        data.parchmentOut != null && data.parchmentOut !== "" ? String(data.parchmentOut) : "";
      this.huskProducedChecked = !!data.wasHuskProduced || data.quantityOfHusk != null;
      this.quantityOfHusk =
        data.quantityOfHusk != null && data.quantityOfHusk !== ""
          ? String(data.quantityOfHusk)
          : "";
      // Re-hydrate existing grading lots so edit shows them; keep id so the backend
      // upserts instead of creating duplicates.
      this.qualityGradingLots = Array.isArray(data.qualityGradings)
        ? data.qualityGradings.map((g) => ({
            id: g.id,
            lotId: g.lotId,
            quantity: g.quantity,
            unitSize: g.unitSize,
            qualityScore: g.qualityScore,
          }))
        : [];
      this.processingBatch.qualityGradingLots = this.qualityGradingLots;
    },
    parseTreeRows(response) {
      if (!response || Number(response.code) !== 200) return [];
      const payload = response.data;
      if (Array.isArray(payload?.rows)) return payload.rows;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(response.rows)) return response.rows;
      return [];
    },
    resolveBatchRating(apiRating) {
      if (!apiRating) return null;
      const mapped = this.ratingMapping[apiRating] || apiRating;
      const options = this.processingBatchQuality || [];
      const match = options.find(
        (option) => option.name === mapped || option.name === apiRating
      );
      return match ? match.name : mapped;
    },
    async loadReferenceData() {
      if (this.referenceDataLoading) return;
      this.referenceDataLoading = true;
      try {
        await Promise.all([this.getProcessTypes(), this.getProcessingBatchQuality()]);
        this.referenceDataLoaded = true;
      } finally {
        this.referenceDataLoading = false;
      }
    },
    async onOpen() {
      await this.loadReferenceData();
      if (this.isAddMode) {
        this.processingBatch = {
          buyingStationId: parseInt(this.resolvedBuyingStationId, 10),
        };
        this.resetProcessingOutputs();
        await this.getAllPurchaseOrders();
      } else if (this.record) {
        await this.initFromRecord(this.record);
      }
    },
    async initFromRecord(rec) {
      const batchId = rec.batchCode || rec.id;
      if (!batchId) return;
      await this.getProcessBatch(batchId);
      if (this.processingBatch.buyingStationId) {
        await this.getAllPurchaseOrders();
      }
    },
    async confirmProcessingBatch() {
      this.startLoading();
      try {
        const payload = this.buildSubmitPayload();

        if (!this.isAddMode && this.processingBatch.id) {
          const { data } = await this.$http.put("coffee/buying-station/processing-batch", payload);
          if (data && data.code === 200) {
            this.$notify({
              title: "Process Batch Updated successfully.",
              type: "success",
            });
          }
        } else {
          const { data } = await this.$http.post("coffee/buying-station/processing-batch", payload);
          if (data && data.code === 200) {
            this.$notify({
              title: "Process Batch Added successfully.",
              type: "success",
            });
          }
        }
        this.confirmVisible = false;
        this.$emit("save");
        this.close();
      } catch (error) {
        this.$notify({
          title: "Process Batch Error.",
          type: "error",
        });
      } finally {
        this.stopLoading();
      }
    },
    async getProcessBatch(id) {
      try {
        const data = await BuyingStationService.getProcessBatch(id);
        if (data.code === 200) {
          this.processingBatch = data.data;
          this.processingBatch.batchRating = this.resolveBatchRating(data.data.batchRating);
          this.processingBatch.processingTypeId =
            data.data.processingTypeId != null ? Number(data.data.processingTypeId) : null;
          this.setProcessingOutputsFromBatch(data.data);
          this.processingBatch.startDate = data.data.startDate
            ? moment(new Date(data.data.startDate)).format("DD-MMM-YYYY")
            : "";
          this.processingBatch.endDate = data.data.endDate
            ? moment(new Date(data.data.endDate)).format("DD-MMM-YYYY")
            : "";
        }
      } catch (error) {
        console.log("getProcessBatch error", error);
      }
    },
    async getAllPurchaseOrders() {
      try {
        this.startLoading();
        const buyingStationId = this.resolvedBuyingStationId;
        let rows = [];

        if (buyingStationId) {
          const res = await BuyingStationService.getAdminPurchaseOrdersByBuyingStation(
            buyingStationId,
            { page: 1, limit: 1000, desc: "true" }
          );
          rows = (res && res.data?.rows) || (res && res.data?.data?.rows) || [];
        } else {
          const res = await BuyingStationService.getAllPurchaseOrders();
          rows = (res && res.data?.rows) || (res && res.rows) || [];
        }

        const availableItems = rows.filter((item) => {
          const availableWeight = item.availableWeight || item.coffeeCherryQty || 0;
          return Number(availableWeight) > 0;
        });

        const usedCodes = Array.isArray(this.usedPurchaseOrders)
          ? this.usedPurchaseOrders
              .map((l) => (typeof l === "string" ? l : l?.orderCode))
              .filter(Boolean)
          : [];

        this.purchaseOrder = availableItems.filter((item) => !usedCodes.includes(item.orderCode));
      } catch (error) {
        console.log("Error fetching purchase orders:", error);
      } finally {
        this.stopLoading();
      }
    },
    async getProcessTypes() {
      try {
        const d = await CoffeeService.getTreeData(
          "processType",
          this.getOrganizationId,
          this.isSubEnterprise ? this.getSubOrganizationId : null
        );
        this.processTypes = this.parseTreeRows(d);
      } catch (err) {
        console.log("Process Types FETCH Error", err);
        this.processTypes = [];
      }
    },
    async getProcessingBatchQuality() {
      try {
        const d = await CoffeeService.getTreeData(
          "processingBatchQuality",
          this.getOrganizationId,
          this.isSubEnterprise ? this.getSubOrganizationId : null
        );
        this.processingBatchQuality = this.parseTreeRows(d);
      } catch (err) {
        console.log("Processing Batch Quality FETCH Error", err);
        this.processingBatchQuality = [];
      }
    },
  },
};
</script>

<style lang="scss">
.processing-batch-add-dialog {
  overflow: visible;
}

.processing-batch-add-dialog__card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  background: #ffffff !important;
}

.processing-batch-add-dialog__form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  background: #ffffff;
}

.processing-batch-add-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background: #ffffff !important;
  -webkit-overflow-scrolling: touch;
}

.processing-batch-add-dialog__footer {
  flex: 0 0 auto;
  background: #ffffff !important;
}

.processing-batch-add-dialog__actions {
  background: #ffffff !important;
}
</style>

<style scoped>
.processing-output-divider-col {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.pb-output-checkbox-row {
  width: fit-content;
}

::v-deep .pb-output-checkbox {
  flex: 0 0 auto;
}

.pb-output-checkbox-label {
  margin-left: -4px;
  user-select: none;
}

::v-deep .process-type-radio-group.v-input {
  margin-top: 8px;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}

::v-deep .process-type-radio-group .v-messages {
  min-height: 0;
}

::v-deep .process-type-radio-group.error--text .v-messages {
  min-height: 14px;
}

::v-deep .process-type-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

::v-deep .process-type-radio-group .v-input--radio-group__input {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

::v-deep .process-type-radio-group .v-radio {
  margin-right: 16px;
  margin-bottom: 8px;
}

::v-deep .process-type-radio-group .v-input--selection-controls__input {
  margin-right: 8px;
}
</style>
